import { showRequestError } from '../../utils/request-feedback';
import { PermissionControl } from '../../permissions/access';
import { Input, Button, message, Select, Form, Row, Col, Drawer, Space, Avatar, Spin, Typography, Radio } from 'antd';
import { fetchPosts } from '../../api/community';
import { useEffect, useState } from 'react';
import { UserOutlined } from '@ant-design/icons';
import { useDebouncedCallback } from 'use-debounce';
import { conversionUtcDate } from '../../utils/comm';
import { createDailyInsights, updateDailyInsights } from '../../api/ai';

const DailyInsightsEditDrawer = ({ open, defaultPost, onClose, onUpdate, isVideo }) => {
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [form] = Form.useForm();
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        form.setFieldValue('search', null);
        if (defaultPost) {
            form.setFieldValue('postId', defaultPost.postId)
            form.setFieldValue('title', defaultPost.articleTitle)
            form.setFieldValue('weight', defaultPost.weight);
            form.setFieldValue('status', defaultPost.status);
            form.setFieldValue('columnName', defaultPost.columnName);
        } else {
            resetFields();
        }
        getPosts('');
    }, [defaultPost]);

    const resetFields = () => {
        form.setFieldValue('search', null);
        form.setFieldValue('postId', '')
        form.setFieldValue('title', '')
        form.setFieldValue('weight', '');
        form.setFieldValue('status', 0);
        form.setFieldValue('columnName', '');
    }

    const onSubmit = () => {
        let params = {
            id: defaultPost?.id,
            postId: form.getFieldValue('postId'),
            weight: form.getFieldValue('weight') ? form.getFieldValue('weight') : 0,
            status: form.getFieldValue('status') ? parseInt(form.getFieldValue('status')) : 0,
            columnName: form.getFieldValue('columnName'),
            articleTitle: form.getFieldValue('title'),
        };
        if (!params.postId) {
            message.warning('Please choose a post');
            return;
        }
        setLoading(true);
        if (params.id) {
            updateDailyInsights(params).then(res => {
                resetFields();

                onClose();
                onUpdate();
            }).finally(() => {
                setLoading(false);
            }).catch(showRequestError)
        } else {
            createDailyInsights(params).then(res => {
                resetFields();

                onClose();
                onUpdate();
            }).finally(() => {
                setLoading(false);
            }).catch(showRequestError)
        }
    }

    const onSelectChange = (value) => {
        form.setFieldValue('postId', value);
        let post = posts.find(item => item.value === value);
        if (post) {
            form.setFieldValue('title', post.label);
        }
    }

    const handleSearch = (value) => {
        getPosts(value);
    }

    const getPosts = useDebouncedCallback(
        (keyword) => {
            let params = {
                'page.num': 1,
                'page.size': 10,
                'keyword': keyword,
                'categoryId': '2',
                'status': 1,
                'isVideo': false
            }
            setSearchLoading(true);
            fetchPosts(params).then(res => {
                if (res.list) {
                    let tmp = res.list.map(item => {
                        let label = item.title;
                        return {
                            avatar: (item.user.picture ? item.user.picture : item.user.avatar),
                            createdAt: conversionUtcDate(item.createdAt, null, true),
                            label: label,
                            value: item.id,
                        }
                    });
                    setPosts(tmp);
                }
            }).finally(() => setSearchLoading(false)).catch(showRequestError)
        },
        300
    )

    const drawTitle = () => {
        return defaultPost ? 'Edit Daily Insights' : 'Add Daily Insights'
    }

    return (
        <div>
            <Drawer
                forceRender={true}
                title={drawTitle()}
                width={480}
                maskClosable
                onClose={onClose}
                open={open}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
                extra={
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <PermissionControl permission="dailyinsights.control.button.46349e65" variant={(defaultPost?.id) ? 'update' : 'create'}><Button onClick={onSubmit} type="primary" loading={loading}>
                            Submit
                        </Button></PermissionControl>
                    </Space>
                }
            >
                <Form
                    layout="vertical"
                    form={form}
                    requiredMark={false} >

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name='search' label='Search'>
                                <Select
                                    style={{ width: '100%' }}
                                    size='large'
                                    placeholder='you can input keywords to search...'
                                    defaultActiveFirstOption={false}
                                    suffixIcon={null}
                                    filterOption={false}
                                    onSearch={handleSearch}
                                    notFoundContent={searchLoading ? <Spin /> : null}
                                    options={posts}
                                    optionRender={(option) => {
                                        return (
                                            <Space>
                                                {
                                                    option.data.avatar ?
                                                        <Avatar size={32} src={option.data.avatar} />
                                                        :
                                                        <Avatar size={32} style={{ backgroundColor: '#87d068' }} icon={<UserOutlined />} />
                                                }
                                                <div style={{ flex: 1 }}>
                                                    <b style={{ wordBreak: 'break-word' }}>{option.data.label ? option.data.label : 'anonymous'}</b>
                                                    <div>
                                                        <Typography.Text style={{ color: '#999', fontSize: 12 }}>
                                                            {option.data.createdAt}
                                                        </Typography.Text>
                                                    </div>
                                                </div>
                                            </Space>
                                        )
                                    }}
                                    onChange={onSelectChange}
                                    allowClear
                                    showSearch
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <div style={{ height: 36, width: 200 }} />
                    
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name='postId' label='Post ID'>
                                <Input placeholder="please input article id" allowClear />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name='title' label='Post Title'>
                                <Input.TextArea autoSize disabled />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name='columnName' label='Column'>
                                <Input placeholder="column name" allowClear />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name='weight' label='Weight'>
                                <Input placeholder="recommend weight" type='number' allowClear />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name='status' label='Status'>
                                <Radio.Group buttonStyle='solid'>
                                    <Radio.Button value={1}>Active</Radio.Button>
                                    <Radio.Button value={0}>Inactive</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Drawer>

            <div style={{ display: 'none' }} id='contentHolder' />
        </div>
    )
}

export default DailyInsightsEditDrawer;